import {
  ActionIcon,
  Box,
  Button,
  Grid,
  Group,
  List,
  Paper,
  Select,
  Stack,
  Text,
  TextInput
} from '@mantine/core'
import { formRootRule, isNotEmpty, useForm } from '@mantine/form'
import { IconPlus, IconTrash } from '@tabler/icons-react'
import { FormattedMessage, useIntl } from 'react-intl'
import { LabelWithHint } from '@/components/ui-shared/LabelWithHint/LabelWithHint'
import { PlatformAlert } from '@/components/ui-shared/PlatformAlert/PlatformAlert'
import { MqttPayloadFormat, MqttTopic } from '@/types/deployment'
import { generateUUID } from '@/utils/uuid'
import { MqttJsonExample } from './MqttJsonExample'

const INTERVAL_OPTIONS = [1, 5, 10, 20, 50, 100]

type MqttTopicWithUuid = MqttTopic & {
  uuid: string
}

type FormValues = {
  topics: MqttTopic[]
}

type InternalFormValues = {
  topics: MqttTopicWithUuid[]
}

type MqttTopicsFormProps = {
  initialValues?: FormValues
  onSubmit: (values: FormValues) => void
  onCancel: () => void
}

export const MqttTopicsForm = ({
  initialValues,
  onSubmit,
  onCancel
}: MqttTopicsFormProps) => {
  const intl = useIntl()

  const initialTopics = (
    initialValues?.topics || [
      {
        type: 'event_log',
        name: '',
        payload_format: MqttPayloadFormat.Default,
        interval: 1
      }
    ]
  ).map((topic) => ({
    ...topic,
    uuid: generateUUID()
  }))

  const form = useForm<InternalFormValues>({
    initialValues: {
      topics: initialTopics
    },
    validate: {
      topics: {
        [formRootRule]: isNotEmpty(
          intl.formatMessage({ id: 'integrations.mqtt.topics.required' })
        ),
        name: (value, values) => {
          const trimmedValue = value.trim()

          if (!trimmedValue) {
            return intl.formatMessage({
              id: 'integrations.mqtt.topics.name.required'
            })
          }

          if (/\s/.test(value)) {
            return intl.formatMessage({
              id: 'integrations.mqtt.topics.name.noSpaces'
            })
          }

          const isDuplicate =
            values.topics.filter((topic) => topic.name.trim() === trimmedValue)
              .length > 1

          if (isDuplicate) {
            return intl.formatMessage({
              id: 'integrations.mqtt.topics.name.unique'
            })
          }

          return null
        }
      }
    }
  })

  const addTopic = () => {
    form.insertListItem('topics', {
      uuid: generateUUID(),
      type: 'event_log',
      name: '',
      payload_format: MqttPayloadFormat.Default,
      interval: 1
    })

    form.clearFieldError('topics')
  }

  const removeTopic = (index: number) => {
    form.removeListItem('topics', index)
    form.clearFieldError('topics')
  }

  const handleSubmit = (values: InternalFormValues) => {
    onSubmit({
      topics: values.topics.map(({ uuid, ...rest }) => rest)
    })
  }

  return (
    <form onSubmit={form.onSubmit((values) => handleSubmit(values))}>
      <PlatformAlert mb="sm" showIcon={false}>
        <Stack gap="xs">
          <Text size="xs">
            <FormattedMessage id="integrations.mqtt.topics.info.line1" />
          </Text>

          <List>
            <List.Item>
              <Text size="xs">
                <FormattedMessage id="integrations.mqtt.topics.type.periodic_log.description" />
              </Text>
            </List.Item>

            <List.Item>
              <Text size="xs">
                <FormattedMessage id="integrations.mqtt.topics.type.event_log.description" />
              </Text>
            </List.Item>
          </List>

          <Text size="xs">
            <FormattedMessage id="integrations.mqtt.topics.info.line2" />
          </Text>
        </Stack>
      </PlatformAlert>

      <Paper p="md" mb="xl" withBorder>
        <MqttJsonExample />
      </Paper>

      {form.errors.topics && (
        <Text c="red" size="xs">
          {form.errors.topics}
        </Text>
      )}

      <Stack gap="sm">
        {form.getValues().topics.map((topic, index) => (
          <Paper key={topic.uuid} p="xs" bg="gray.1" radius="xs">
            <Grid gutter={4}>
              <Grid.Col span={2}>
                <Select
                  size="xs"
                  allowDeselect={false}
                  label={
                    <FormattedMessage id="integrations.mqtt.topics.type" />
                  }
                  data={[
                    {
                      label: intl.formatMessage({
                        id: 'integrations.mqtt.topics.type.periodic_log.label'
                      }),
                      value: 'periodic_log'
                    },
                    {
                      label: intl.formatMessage({
                        id: 'integrations.mqtt.topics.type.event_log.label'
                      }),
                      value: 'event_log'
                    }
                  ]}
                  {...form.getInputProps(`topics.${index}.type`)}
                />
              </Grid.Col>

              <Grid.Col span={5}>
                <TextInput
                  size="xs"
                  label={
                    <LabelWithHint
                      label={
                        <FormattedMessage id="integrations.mqtt.topics.name" />
                      }
                      hint={
                        <FormattedMessage id="integrations.mqtt.topics.name.hint" />
                      }
                    />
                  }
                  autoFocus={index === 0}
                  {...form.getInputProps(`topics.${index}.name`)}
                />
              </Grid.Col>

              <Grid.Col span={2}>
                <Select
                  size="xs"
                  label={
                    <LabelWithHint
                      label={
                        <FormattedMessage id="integrations.mqtt.topics.format.label" />
                      }
                      hint={
                        <FormattedMessage id="integrations.mqtt.topics.format.hint" />
                      }
                    />
                  }
                  data={[
                    {
                      label: intl.formatMessage({
                        id: 'integrations.mqtt.topics.format.default.label'
                      }),
                      value: String(MqttPayloadFormat.Default)
                    },
                    {
                      label: intl.formatMessage({
                        id: 'integrations.mqtt.topics.format.oinsights.label'
                      }),
                      value: String(MqttPayloadFormat.Oinsights)
                    }
                  ]}
                  allowDeselect={false}
                  value={String(topic.payload_format)}
                  onChange={(value) => {
                    form.setFieldValue(
                      `topics.${index}.payload_format`,
                      Number(value)
                    )
                  }}
                />
              </Grid.Col>

              <Grid.Col span={3}>
                <Group wrap="nowrap" align="start" gap="xs">
                  {topic.type === 'periodic_log' && (
                    <Select
                      size="xs"
                      w="100%"
                      label={
                        <LabelWithHint
                          label={
                            <FormattedMessage id="integrations.mqtt.topics.interval" />
                          }
                          hint={
                            <FormattedMessage id="integrations.mqtt.topics.interval.hint" />
                          }
                        />
                      }
                      data={INTERVAL_OPTIONS.map((value) => ({
                        label: intl.formatMessage(
                          {
                            id: 'integrations.mqtt.topics.interval.frames'
                          },
                          {
                            value
                          }
                        ),
                        value: String(value)
                      }))}
                      allowDeselect={false}
                      value={String(topic.interval)}
                      onChange={(value) => {
                        form.setFieldValue(
                          `topics.${index}.interval`,
                          Number(value)
                        )
                      }}
                    />
                  )}

                  <ActionIcon
                    variant="subtle"
                    size="sm"
                    mt={28}
                    radius="xl"
                    onClick={() => removeTopic(index)}
                  >
                    <IconTrash style={{ width: '70%', height: '70%' }} />
                  </ActionIcon>
                </Group>
              </Grid.Col>
            </Grid>
          </Paper>
        ))}

        <Box>
          <Button
            variant="subtle"
            size="compact-sm"
            leftSection={<IconPlus size={16} />}
            onClick={addTopic}
          >
            <FormattedMessage id="integrations.mqtt.topics.addTopic" />
          </Button>
        </Box>
      </Stack>

      <Group justify="end" mt="xl">
        <Button miw={100} size="xs" variant="default" onClick={onCancel}>
          <FormattedMessage id="cancel" />
        </Button>

        <Button miw={100} type="submit" size="xs">
          <FormattedMessage id="save" />
        </Button>
      </Group>
    </form>
  )
}
